import {
  CheckCircleFilled,
  LoadingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, Modal, Select, Spin } from "antd";
import React, { useEffect, useState } from "react";
import AddUserApi from "../../../api/users/AddUserApi";
import CheckUsernameApi from "../../../api/users/CheckUsernameApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../helpers/NotificationHelpers";
import { GetAvailablePositionsApi } from "../../../api/positions/GetAvailablePositionsApi";
import { useSelector } from "react-redux";
import { GetApproversApi } from "../../../api/approvals/GetApproversApi";

const { Option } = Select;

function AddUserModal(props) {
  const [submit, setSubmit] = useState(false);
  const [positions, setPositions] = useState([]);
  const [unitsTree, setUnitsTree] = useState([]);
  const [check, setCheck] = useState(false);
  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState("");
  const [checking, setChecking] = useState(false);
  const positionState = useSelector((state) => state.positionState.value);
  const [approvers, setApprovers] = useState([]);

  function handleCancel() {
    setSubmit(false);
    setPositions([]);
    setUnitsTree([]);
    setCheck(false);
    setUsername("");
    setUsernameStatus("");
    setChecking(false);
    props.handleCancel();
  }

  useEffect(() => {
    if (props.visible) {
      getPositions();
      getApprovers();
    }
  }, [props.visible]);

  console.log(approvers);

  async function getPositions() {
    const apiResponse = await GetAvailablePositionsApi();
    if (apiResponse.success) {
      setPositions(apiResponse.data);
    }
  }

  function editUsername(name) {
    setUsername(name);
    setUsernameStatus("");
    if (name) {
      setCheck(true);
    } else {
      setCheck(false);
    }
  }

  async function checkUsername() {
    setChecking(true);
    setUsernameStatus("");
    const apiResponse = await CheckUsernameApi(username);
    if (apiResponse.success) {
      if (apiResponse.data?.canAdd) {
        setUsernameStatus("success");
      } else {
        setUsernameStatus("error");
      }
      setChecking(false);
    }
  }

  async function onFinish(values) {
    setSubmit(true);

    if (usernameStatus === "success") {
      const apiResponse = await AddUserApi(
        values.employeeId,
        username,
        values.email,
        values.position || null,
        positionState.id,
        positionState?.userRole?.name === "Manager"
          ? values.approver
          : positionState.id
      );

      if (apiResponse.error) {
        openNotificationError("bottomRight", apiResponse.message);
      } else if (apiResponse.success) {
        openNotificationSuccess("bottomRight", apiResponse.message);
        props.handleOk();
      } else {
        openNotificationError(
          "bottomRight",
          "Couldn't add user try again later."
        );
      }
      handleCancel();
    } else {
      setSubmit(false);
    }
  }

  async function getApprovers() {
    const apiResponse = await GetApproversApi(positionState?.id);

    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      setApprovers(apiResponse?.data);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't edit approver. Try again later."
      );
    }
  }

  return (
    <Modal
      title="Add new user"
      visible={props.visible}
      onOk={props.handleOk}
      onCancel={() => handleCancel()}
      footer={null}
      destroyOnClose={true}
    >
      <Form
        name="addUser"
        onFinish={onFinish}
        autoComplete="off"
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          label="Employee Id*"
          name="employeeId"
          rules={[
            {
              required: true,
              message: "Please enter employee Id!",
            },
          ]}
        >
          <Input placeholder="Employee Id" />
        </Form.Item>

        <Form.Item label="Username*" name="username">
          <Input.Group compact>
            <Input
              disabled={checking}
              style={{ width: "calc(100% - 90px)" }}
              placeholder="Username"
              onChange={(e) => editUsername(e.target.value)}
              suffix={
                usernameStatus === "success" ? (
                  <div className="text-green-500 text-xs">
                    <CheckCircleFilled /> Available
                  </div>
                ) : (
                  usernameStatus === "error" && (
                    <div className="text-red-500 text-xs">
                      <WarningOutlined /> Already exist
                    </div>
                  )
                )
              }
            />
            <Button
              onClick={() => checkUsername()}
              disabled={!check || usernameStatus === "success"}
              style={{ width: "90px" }}
              type="primary"
            >
              {checking ? (
                <Spin
                  size="small"
                  indicator={<LoadingOutlined style={{ color: "#fff" }} spin />}
                />
              ) : (
                "Check"
              )}
            </Button>
          </Input.Group>
        </Form.Item>

        <Form.Item
          label="Email*"
          name="email"
          rules={[
            {
              required: true,
              message: "Please enter an email !",
            },
          ]}
        >
          <Input placeholder="Email" />
        </Form.Item>

        <Form.Item label="Position" name="position">
          <Select
            showSearch
            placeholder="Select position"
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) =>
              optionA.children
                .toLowerCase()
                .localeCompare(optionB.children.toLowerCase())
            }
          >
            {positions.map((position) => {
              return (
                <Option key={position.id} value={position.id}>
                  {position.designation + ", " + position.unit}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
        {positionState?.userRole?.name === "Manager" && (
          <Form.Item
            label="Approver"
            name="approver"
            rules={[
              {
                required: true,
                message: "Please select approver!",
              },
            ]}
          >
            <Select
              allowClear
              showSearch
              placeholder="Select approver"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              filterSort={(optionA, optionB) =>
                optionA.children
                  .toLowerCase()
                  .localeCompare(optionB.children.toLowerCase())
              }
            >
              {approvers?.map((approver) => {
                return (
                  <Option key={approver?.id} value={approver?.id}>
                    {(approver?.designations?.abbreviation ||
                      approver?.designations?.name) +
                      "," +
                      approver?.units?.name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <div className=" flex justify-end">
            <Button className="mr-3 w-28" onClick={() => handleCancel()}>
              Cancel
            </Button>
            <Button
              className="w-28"
              type="primary"
              htmlType={submit && "submit"}
              disabled={usernameStatus != "success"}
            >
              {submit ? (
                <Spin
                  size="small"
                  indicator={<LoadingOutlined style={{ color: "#fff" }} spin />}
                />
              ) : (
                "Invite user"
              )}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default AddUserModal;
